import { supabase } from '../lib/supabase'
import { apiRequestPublic, apiRequestProtected, ApiClientError } from '../utils/axios'

export interface AuthUser {
  id: string
  email: string
  name?: string
  role?: string
  lastLogin?: Date
  isEmailVerified: boolean
}

export interface AuthSession {
  isValid: boolean
  expiresAt?: Date
  accessToken?: string
  user?: any // Full Supabase user object with metadata
}

export interface LoginResponse {
  access_token: string
}

export interface LoginWithSupabaseResponse {
  user: AuthUser
  session: AuthSession
  accessToken: string
}

/**
 * Login with email and password using Supabase
 * Stores Supabase token directly - no token exchange needed
 * Falls back to legacy login if Supabase fails
 */
export async function loginWithSupabase(email: string, password: string): Promise<LoginWithSupabaseResponse> {
  try {
    // First, authenticate with Supabase
    const { data: supabaseAuth, error: supabaseError } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (supabaseError || !supabaseAuth.session) {
      console.error('❌ Supabase login failed:', supabaseError?.message)
      throw new Error(supabaseError?.message || 'Failed to authenticate with Supabase')
    }

    // Store Supabase token directly in localStorage
    const supabaseToken = supabaseAuth.session.access_token
    localStorage.setItem('token', supabaseToken)
    localStorage.setItem('email', email)

    // Create user object
    const user: AuthUser = {
      id: supabaseAuth.user?.id || email,
      email: email,
      name: email.split('@')[0],
      role: 'customer',
      lastLogin: new Date(),
      isEmailVerified: true,
    }

    // Create session object
    const session: AuthSession = {
      isValid: true,
      expiresAt: supabaseAuth.session.expires_at ? new Date(supabaseAuth.session.expires_at * 1000) : undefined,
      accessToken: supabaseToken,
    }

    // Sign-in happens against Supabase directly, so the server only learns
    // about it if we tell it. Best-effort: never let this fail the login.
    void recordLogin()

    return { user, session, accessToken: supabaseToken }
  } catch (error: any) {
    console.error('❌ Login with Supabase failed:', error)

    // Fall back to legacy login if Supabase fails
    try {
      const loginResponse = await legacyLogin(email, password)
      const user: AuthUser = {
        id: email,
        email: email,
        name: email.split('@')[0],
        role: 'customer',
        lastLogin: new Date(),
        isEmailVerified: true,
      }
      const session: AuthSession = { isValid: true, accessToken: loginResponse.access_token }
      return { user, session, accessToken: loginResponse.access_token }
    } catch (fallbackError) {
      console.error('❌ Fallback login also failed:', fallbackError)
      throw error // Throw original error if fallback also fails
    }
  }
}

/**
 * Tell the API a login happened so it can bump the user's login counter.
 * Failures are swallowed — this is telemetry, not part of authentication.
 */
async function recordLogin(): Promise<void> {
  try {
    await apiRequestProtected('POST', '/auth/record-login')
  } catch (error) {
    console.warn('Could not record login count:', error)
  }
}

/**
 * Legacy login with email and password (direct API call)
 */
async function legacyLogin(email: string, password: string): Promise<LoginResponse> {
  const response = await apiRequestPublic('POST', '/login', {
    email,
    password,
  })

  // Handle different response structures from legacy API
  let accessToken: string
  if (response.access_token) {
    accessToken = response.access_token
  } else if (response.token) {
    accessToken = response.token
  } else if (response.accessToken) {
    accessToken = response.accessToken
  } else if (response.jwt) {
    accessToken = response.jwt
  } else {
    console.error('No access token found in response:', response)
    throw new Error('Authentication failed: No token received from server')
  }

  // Store token in localStorage
  localStorage.setItem('token', accessToken)
  localStorage.setItem('email', email)

  return { access_token: accessToken }
}

/**
 * @deprecated Token exchange is no longer needed - backend accepts Supabase JWTs directly
 * This function now just returns the Supabase token as-is for backward compatibility
 */
export async function exchangeSupabaseToken(supabaseAccessToken: string): Promise<LoginResponse> {
  console.warn('⚠️ exchangeSupabaseToken is deprecated - backend now accepts Supabase JWTs directly')

  // Just return the Supabase token as-is
  localStorage.setItem('token', supabaseAccessToken)
  return { access_token: supabaseAccessToken }
}

/**
 * Sign out from both Supabase and clear legacy tokens
 */
export async function signOut(): Promise<void> {
  try {
    // Sign out from Supabase
    await supabase.auth.signOut()
  } catch (error) {
    console.error('Error signing out from Supabase:', error)
  }

  // Clear localStorage
  localStorage.removeItem('token')
  localStorage.removeItem('email')
  localStorage.removeItem('username')

  // Clear sessionStorage
  sessionStorage.clear()
}

/**
 * Verify user's current password by attempting re-authentication
 * Used before password change operations
 */
export async function verifyCurrentPassword(email: string, password: string): Promise<boolean> {
  try {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      console.error('Password verification failed:', error.message)
      return false
    }

    return true
  } catch (error) {
    console.error('Error verifying password:', error)
    return false
  }
}

/**
 * Change user's password after verifying current password
 */
export async function changePassword(
  email: string,
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> {
  // Step 1: Verify current password
  const isValid = await verifyCurrentPassword(email, currentPassword)
  if (!isValid) {
    return { success: false, error: 'Current password is incorrect' }
  }

  // Step 2: Update to new password
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  })

  if (error) {
    return { success: false, error: error.message }
  }

  return { success: true }
}

/**
 * Reset password for email
 */
export async function resetPasswordForEmail(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  })

  if (error) {
    throw new Error(error.message)
  }
}

/**
 * Get current Supabase session
 * Stores Supabase token directly - no token exchange
 */
export async function getCurrentSession(): Promise<AuthSession | null> {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession()

    if (error) {
      console.error('Error getting session:', error)
      return null
    }

    if (!session) {
      return null
    }

    // Store Supabase token directly if we don't have one
    const storedToken = localStorage.getItem('token')
    if (!storedToken && session.access_token) {
      localStorage.setItem('token', session.access_token)
    }

    return {
      isValid: true,
      expiresAt: session.expires_at ? new Date(session.expires_at * 1000) : undefined,
      accessToken: storedToken || session.access_token,
      user: session.user,
    }
  } catch (error) {
    console.error('Error in getCurrentSession:', error)
    return null
  }
}

/**
 * Helper to detect refresh token errors that indicate an unrecoverable session
 */
function isRefreshTokenError(error: any): boolean {
  const message = error?.message?.toLowerCase() || ''
  return (
    message.includes('refresh token not found') ||
    message.includes('invalid refresh token') ||
    error?.code === 'refresh_token_not_found'
  )
}

/**
 * Refresh the current Supabase session
 * This ensures we have a valid, up-to-date JWT token
 * Should be called on app initialization to prevent "Invalid Supabase JWT token" errors
 */
export async function refreshSession(): Promise<AuthSession | null> {
  try {
    // First check if there's an existing session
    // This prevents "Auth session missing" errors when user is logged out
    const {
      data: { session: currentSession },
    } = await supabase.auth.getSession()

    // If no session exists, return null without trying to refresh
    if (!currentSession) {
      return null
    }

    // Session exists, try to refresh it
    const {
      data: { session },
      error,
    } = await supabase.auth.refreshSession()

    if (error) {
      console.error('Error refreshing session:', error)

      // Check for definitive refresh token failures - these mean the session is unrecoverable
      if (isRefreshTokenError(error)) {
        console.warn('Refresh token is invalid/expired, clearing stored tokens')
        localStorage.removeItem('token')
        localStorage.removeItem('email')
        localStorage.removeItem('username')
        // Sign out from Supabase to clear any stale state
        try {
          await supabase.auth.signOut({ scope: 'local' })
        } catch {
          // Ignore signOut errors, we're already in a bad state
        }
        return null
      }

      // For other errors, try to get the current session as fallback
      return await getCurrentSession()
    }

    if (!session) {
      return null
    }

    // Always update the token in localStorage with the fresh token
    localStorage.setItem('token', session.access_token)

    // Also update email if we have it
    if (session.user?.email) {
      localStorage.setItem('email', session.user.email)
    }

    return {
      isValid: true,
      expiresAt: session.expires_at ? new Date(session.expires_at * 1000) : undefined,
      accessToken: session.access_token,
      user: session.user,
    }
  } catch (error: any) {
    console.error('Error in refreshSession:', error)

    // Check for definitive refresh token failures in catch block too
    if (isRefreshTokenError(error)) {
      console.warn('Refresh token is invalid/expired (caught), clearing stored tokens')
      localStorage.removeItem('token')
      localStorage.removeItem('email')
      localStorage.removeItem('username')
      try {
        await supabase.auth.signOut({ scope: 'local' })
      } catch {
        // Ignore signOut errors
      }
      return null
    }

    // Fallback to getting current session for other errors
    return await getCurrentSession()
  }
}

/**
 * Subscribe to auth state changes
 * Stores Supabase token directly - no token exchange
 */
export function onAuthStateChange(callback: (event: string, session: AuthSession | null) => void): {
  unsubscribe: () => void
} {
  const { data: authListener } = supabase.auth.onAuthStateChange(async (event, supabaseSession) => {
    if (!supabaseSession) {
      callback(event, null)
      return
    }

    // Store Supabase token directly on SIGNED_IN or TOKEN_REFRESHED events
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      localStorage.setItem('token', supabaseSession.access_token)
    }

    // Convert Supabase session to our AuthSession format
    const session: AuthSession = {
      isValid: true,
      expiresAt: supabaseSession.expires_at ? new Date(supabaseSession.expires_at * 1000) : undefined,
      accessToken: supabaseSession.access_token,
      user: supabaseSession.user,
    }

    callback(event, session)
  })

  return {
    unsubscribe: () => {
      authListener.subscription.unsubscribe()
    },
  }
}

/**
 * Check if user is authenticated (has valid token)
 */
export function isAuthenticated(): boolean {
  const token = localStorage.getItem('token')
  const email = localStorage.getItem('email')
  return !!(token && email && token !== 'undefined' && token !== 'null')
}

/**
 * Get current user email from localStorage
 */
export function getCurrentUserEmail(): string | null {
  return localStorage.getItem('email')
}

/**
 * Get auth token from localStorage
 */
export function getAuthToken(): string | null {
  const token = localStorage.getItem('token')
  if (!token || token === 'undefined' || token === 'null') {
    return null
  }
  return token
}

/**
 * Validate current session with protected endpoint
 */
export async function validateSession(): Promise<boolean> {
  if (!isAuthenticated()) {
    return false
  }

  try {
    await apiRequestProtected('GET', '/healthcheck-protected')
    return true
  } catch (error) {
    if (error instanceof ApiClientError && error.statusCode === 401) {
      // Token is invalid, clear it
      localStorage.removeItem('token')
      localStorage.removeItem('email')
    }
    return false
  }
}

/**
 * Auth service object for backward compatibility
 */
export const authService = {
  loginWithSupabase,
  signOut,
  resetPasswordForEmail,
  verifyCurrentPassword,
  changePassword,
  getSession: getCurrentSession,
  refreshSession,
  exchangeSupabaseToken,
  onAuthStateChange,
  isAuthenticated,
  getCurrentUserEmail,
  getAuthToken,
  validateSession,
}
